import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SubscriptionCollectionService } from '@core/services/subscription-collection.service';

/** Os avisos que disputam o topo da casca, do mais ao menos prioritário. */
export type AvisoDoTopo = 'subscription' | 'maintenance' | 'incident' | 'certificate';

/**
 * Regra do dono (06/10/2026): só UM aviso visível por vez, escolhido por
 * prioridade. Este é o único ponto que decide; quem desenha pergunta
 * `current()`/`visible(...)` em vez de combinar `@if` por conta própria.
 *
 *  1. `subscription`: bloqueio, suspensão ou pendência da assinatura;
 *  2. `maintenance`: faixa de manutenção da aplicação;
 *  3. `incident`: faixa de incidente ativo da central de suporte;
 *  4. `certificate`: oferta/renovação do certificado digital, só com tudo
 *     acima apagado e com a consulta de incidentes já resolvida.
 *
 * Shared with the dashboard through the layout injector; reset on leaving the layout.
 */
@Injectable()
export class SystemNoticeState {
  private readonly state = signal<'loading' | 'clear' | 'active'>('loading');
  private readonly collection = inject(SubscriptionCollectionService).collection;
  private readonly collectionSignal = toSignal(this.collection, { initialValue: this.collection.value });

  readonly maintenanceActive = signal(false);
  readonly subscriptionActive = computed(() => {
    const collection = this.collectionSignal();
    return !!collection && collection.stage !== 'current';
  });

  readonly current = computed<AvisoDoTopo | null>(() => {
    if (this.subscriptionActive()) return 'subscription';
    if (this.maintenanceActive()) return 'maintenance';
    if (this.state() === 'active') return 'incident';
    if (this.state() === 'clear') return 'certificate';
    return null;
  });

  readonly certificateOfferAllowed = computed(() => this.current() === 'certificate');

  visible(aviso: AvisoDoTopo): boolean {
    return this.current() === aviso;
  }

  update(activeCount: number): void {
    this.state.set(activeCount > 0 ? 'active' : 'clear');
  }
}
